import { useAtom } from "jotai";
import { FC, ReactNode, useEffect, useMemo, useRef } from "react";

import { dimensionsAtom } from "src/components/DimensionedLayout/dimensionsAtom.ts";
import useRefDimensions from "src/components/DimensionedLayout/useRefDimensions.ts";
import { defaultPageSize, defaultRowHeight } from "src/config/table-config.ts";

//TODO: There is a new ticket for this to move this logic into shared libs
//It is for pagination, it calculates limit value for a table based on dimensions
export type DimensionedLayoutProps = {
  children: ReactNode;
  /**
   * If true, applies full-height CSS class (100vh - 11rem) for full-page layouts.
   * If false, lets the parent container control the height (useful for modals).
   * @default true
   */
  fullHeight?: boolean;
  /**
   * Optional className to add to the container
   */
  className?: string;
};

export const DimensionedLayout: FC<DimensionedLayoutProps> = ({
  children,
  fullHeight = true,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const dimensions = useRefDimensions(ref);

  const [_, setPageSize] = useAtom(dimensionsAtom);
  /*
   * Sometimes, the header displays an element that is only partially visible at certain heights.
   * Therefore, we need to add one more element. That's why I ignored the height.
   */
  const calculatedPageSize = useMemo(() => {
    if (!dimensions?.height || dimensions.height < defaultRowHeight) {
      return defaultPageSize;
    }
    return Math.round(dimensions.height / defaultRowHeight);
  }, [dimensions?.height]);

  useEffect(() => {
    setPageSize({ pageSize: calculatedPageSize, dimensions: dimensions });
  }, [calculatedPageSize, setPageSize, dimensions]);

  return (
    <div
      ref={ref}
      className={fullHeight ? `full-height ${className || ""}`.trim() : className || ""}
      style={
        !fullHeight && className?.includes("flex-1")
          ? { display: "flex", flexDirection: "column", minHeight: 0 }
          : undefined
      }
    >
      {children}
    </div>
  );
};
